import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Mail, ArrowRight } from 'lucide-react';

const TeamPage = () => {
  useEffect(() => {
    document.title = 'Team | Nexus LBE';
  }, []);

  const team = [
    {
      name: 'Geoffrey Goodwin',
      title: '',
      bio: 'Business leader, executive producer, and strategist with experience at BBC, Verizon, and Outernet. Chair of LIPA (Liverpool Institute for Performing Arts). Deep expertise in media, creative technology, business strategy, and production. Adept at audience-centric design and building high-performing teams.',
      email: 'geoffrey@nexuslbe.com',
      linkedin: 'https://www.linkedin.com/in/geoffreygoodwin',
      photo: '/images/geoffrey-v2.png'
    },
    {
      name: 'Julian Holland',
      title: '',
      bio: 'Award-winning media executive with extensive experience in content production and creative technology. Operates at the forefront of content production with cutting-edge technologies. Proven management track record in recruiting, building, and leading multidisciplinary teams for state-of-the-art execution.',
      email: 'julian@nexuslbe.com',
      linkedin: 'https://www.linkedin.com/in/julianholland',
      photo: '/images/julian-v2.png'
    }
  ];

  const strengths = [
    'Deep experience in media, creative technology, business strategy, and production',
    'EMEA relationships for site acquisition, fit-out, and venue operations',
    'Ability to craft engaging, repeatable stories with emotional resonance',
    'Operate at forefront of content production with cutting-edge technologies',
    'Proven management track record in recruiting and leading multidisciplinary teams'
  ];

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-10 lg:pt-20 lg:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <Badge variant="outline" className="mb-4">Our Team</Badge>
          <h1 className="font-['Geist'] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-6">
            Built by media and experience veterans
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto">
            Combining decades of expertise in media production, creative technology, and business strategy 
            to build the next generation of LBE experiences.
          </p>
        </div>
      </section>

      {/* Founders */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="space-y-8">
            {team.map((member, idx) => (
              <Card key={idx}>
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-4 gap-6">
                    <div className="md:col-span-1 space-y-4">
                      <div className="relative rounded-xl overflow-hidden aspect-square bg-slate-800">
                        <img 
                          src={member.photo} 
                          alt={`${member.name} - NEXUS LBE team member`}
                          className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300"
                        />
                      </div>
                      <div>
                        <h3 className="font-['Geist'] text-2xl mb-3">{member.name}</h3>
                        <div className="space-y-2">
                        <a 
                          href={`mailto:${member.email}`}
                          className="inline-flex items-center gap-2 text-sm text-primary hover:text-[hsl(17_62%_34%)] transition-colors"
                        >
                          <Mail className="h-4 w-4" />
                          {member.email}
                        </a>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <p className="text-muted-foreground leading-relaxed">
                        {member.bio}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Strengths */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              What we bring
            </h2>
          </div>
          
          <Card>
            <CardContent className="p-8 md:p-12">
              <ul className="space-y-4">
                {strengths.map((strength, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                    <span className="text-muted-foreground leading-relaxed flex-1">{strength}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Advisory Board */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Advisory board in development
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            We're building an advisory board with expertise in law, GCC markets, real estate, and venture capital 
            to guide our global expansion.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Badge className="bg-[#00F5FF] text-[#0F0B1F] hover:bg-[#00F5FF]/90">Legal & Compliance</Badge>
            <Badge className="bg-[#00F5FF] text-[#0F0B1F] hover:bg-[#00F5FF]/90">GCC Market Expertise</Badge>
            <Badge className="bg-[#00F5FF] text-[#0F0B1F] hover:bg-[#00F5FF]/90">Commercial Real Estate</Badge>
            <Badge className="bg-[#00F5FF] text-[#0F0B1F] hover:bg-[#00F5FF]/90">Venture Capital</Badge>
            <Badge className="bg-[#00F5FF] text-[#0F0B1F] hover:bg-[#00F5FF]/90">Entertainment IP</Badge>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Want to work with us?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            We're always interested in connecting with experienced professionals in media, technology, operations, and real estate.
          </p>
          <Link to="/contact">
            <Button size="lg" className="h-12 px-6 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-[#0F0B1F]">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
